import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '@/components/feature/SuperAdminLayout';
import SAHeader from '@/components/feature/SAHeader';
import LoadingState from '@/components/base/LoadingState';
import ErrorState from '@/components/base/ErrorState';
import { supabase } from '@/lib/supabaseClient';
import { requireSuperAdmin } from '@/middleware/superAdminGuard';

/* ─── Types ──────────────────────────────────────────────────── */
interface TableInfo {
  table_name: string;
  table_schema: string;
  has_tenant_id: boolean;
  has_rls: boolean;
}

interface StorageBucket {
  name: string;
  public: boolean;
}

type RiskLevel = 'HIGH' | 'MEDIUM' | 'LOW';

interface AuditRow {
  table_name: string;
  table_schema: string;
  has_rls: boolean;
  has_tenant_id: boolean;
  risk_level: RiskLevel;
}

/* ─── Constants ──────────────────────────────────────────────── */
const RISK_CONFIG: Record<RiskLevel, { bg: string; border: string; text: string; dot: string; label: string }> = {
  HIGH:   { bg: 'bg-rose-50',   border: 'border-rose-100',   text: 'text-rose-700',   dot: 'bg-rose-500',   label: 'HIGH' },
  MEDIUM: { bg: 'bg-amber-50',  border: 'border-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500',  label: 'MEDIUM' },
  LOW:    { bg: 'bg-emerald-50', border: 'border-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'LOW' },
};

/* ─── Hardcoded schema fallback ─────────────────────────────── */
const FALLBACK_TABLES: TableInfo[] = [
  { table_name: 'activity_logs',                table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'ai_conversations',             table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'ai_messages',                  table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'appointments',                 table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'audit_logs',                   table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'automation_rules',             table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'client_access_tokens',         table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'client_addons',                table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'client_invitation_templates',  table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'client_messages',              table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'clients',                      table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'contract_addons',              table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'contract_charges',             table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'contract_templates',           table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'contracts',                    table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'digital_invitations',          table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'email_queue',                  table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'event_route_stops',            table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'event_routes',                 table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'invitation_music',             table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'invitation_rsvps',             table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'invitation_templates',         table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'job_deliveries',               table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'job_notes',                    table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'job_photos',                   table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'job_steps',                    table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'job_tasks',                    table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'jobs',                         table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'lead_notes',                   table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'marketing_leads',              table_schema: 'public', has_tenant_id: false, has_rls: true },
  { table_name: 'media_package_files',          table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'media_packages',               table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'notifications',                table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'payments',                     table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'reminders',                    table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'saas_global_settings',         table_schema: 'public', has_tenant_id: false, has_rls: true },
  { table_name: 'saas_modules',                 table_schema: 'public', has_tenant_id: false, has_rls: true },
  { table_name: 'saas_plan_modules',            table_schema: 'public', has_tenant_id: false, has_rls: true },
  { table_name: 'saas_plans',                   table_schema: 'public', has_tenant_id: false, has_rls: true },
  { table_name: 'saas_settings',                table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'saas_super_admins',            table_schema: 'public', has_tenant_id: false, has_rls: true },
  { table_name: 'saas_tenant_branding',         table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'saas_tenant_users',            table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'saas_tenants',                 table_schema: 'public', has_tenant_id: false, has_rls: true },
  { table_name: 'signature_book_entries',       table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'signature_book_photos',        table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'signature_books',              table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'tenant_ai_settings',           table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'tenant_domain_requests',       table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'tenant_gallery_items',         table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'tenant_integrations',          table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'tenant_module_requests',       table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'tenant_modules',               table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'tenant_public_leads',          table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'tenant_services',              table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'tenant_website_content',       table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'uploaded_files',               table_schema: 'public', has_tenant_id: true,  has_rls: true },
  { table_name: 'whatsapp_queue',               table_schema: 'public', has_tenant_id: true,  has_rls: true },
];

/* ─── Risk engine ────────────────────────────────────────────── */
function assessRisk(t: TableInfo): RiskLevel {
  if (!t.has_rls) return 'HIGH';
  if (!t.has_tenant_id) {
    const saTables = ['saas_tenants', 'saas_super_admins', 'saas_plans', 'saas_modules', 'saas_plan_modules', 'saas_global_settings', 'marketing_leads'];
    if (saTables.includes(t.table_name)) return 'MEDIUM';
    return 'HIGH';
  }
  return 'LOW';
}

/* ─── Section definitions ───────────────────────────────────── */
interface SectionDef {
  id: string;
  title: string;
  icon: string;
  color: string;
  bg: string;
  border: string;
  filter: (rows: AuditRow[]) => AuditRow[];
}

const SECTIONS: SectionDef[] = [
  {
    id: 'with-tenant',
    title: 'Tables with tenant_id',
    icon: 'ri-shield-check-line',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
    filter: (rows) => rows.filter((r) => r.has_tenant_id),
  },
  {
    id: 'without-tenant',
    title: 'Tables without tenant_id',
    icon: 'ri-shield-flash-line',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-100',
    filter: (rows) => rows.filter((r) => !r.has_tenant_id),
  },
  {
    id: 'rls-on',
    title: 'RLS Enabled',
    icon: 'ri-lock-line',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
    filter: (rows) => rows.filter((r) => r.has_rls),
  },
  {
    id: 'rls-off',
    title: 'Missing RLS',
    icon: 'ri-alert-line',
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    border: 'border-rose-100',
    filter: (rows) => rows.filter((r) => !r.has_rls),
  },
  {
    id: 'public',
    title: 'Public Tables',
    icon: 'ri-global-line',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-100',
    filter: (rows) => rows.filter((r) => !r.has_rls),
  },
];

/* ─── Component ──────────────────────────────────────────────── */
export default function SecurityAuditPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [buckets, setBuckets] = useState<StorageBucket[]>([]);
  const [bucketsError, setBucketsError] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [metadataUnavailable, setMetadataUnavailable] = useState(false);

  /* ── Fetch ── */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    setMetadataUnavailable(false);
    setBucketsError(null);

    // Attempt live metadata from Supabase
    let liveTables: TableInfo[] | null = null;
    try {
      const { data: metaData, error: metaErr } = await supabase
        .from('information_schema.tables')
        .select('table_name, table_schema')
        .eq('table_schema', 'public')
        .order('table_name');

      if (!metaErr && metaData && metaData.length > 0) {
        // Got live metadata — enhance with RLS and tenant_id checks
        setMetadataUnavailable(false);
        // We'd need additional queries for RLS status and column checks,
        // but those also require elevated privileges from the frontend.
        // Fall through to hardcoded for full analysis.
        liveTables = null;
      }
    } catch {
      // Expected: anon key cannot access information_schema
    }

    // If live metadata failed (expected), flag it and use fallback
    if (!liveTables) {
      setMetadataUnavailable(true);
      setTables(FALLBACK_TABLES);
    }

    // Try storage buckets
    try {
      const { data: bucketList, error: bucketErr } = await supabase.storage.listBuckets();
      if (!bucketErr && bucketList) {
        setBuckets(bucketList.map((b: { name: string; public: boolean }) => ({
          name: b.name,
          public: b.public ?? false,
        })));
      } else {
        setBucketsError(bucketErr?.message ?? 'Cannot list buckets');
      }
    } catch {
      setBucketsError('Storage API not accessible');
    }

    setLastChecked(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const ok = await requireSuperAdmin();
      if (!ok) { navigate('/login'); return; }
      await fetchAll();
    };
    init();
  }, [navigate, fetchAll]);

  /* ── Derived ── */
  const rows: AuditRow[] = tables.map((t) => ({
    ...t,
    risk_level: assessRisk(t),
  }));

  const kpis = {
    total: rows.length,
    withTenant: rows.filter((r) => r.has_tenant_id).length,
    withoutTenant: rows.filter((r) => !r.has_tenant_id).length,
    rlsOn: rows.filter((r) => r.has_rls).length,
    rlsOff: rows.filter((r) => !r.has_rls).length,
    highRisk: rows.filter((r) => r.risk_level === 'HIGH').length,
    mediumRisk: rows.filter((r) => r.risk_level === 'MEDIUM').length,
    lowRisk: rows.filter((r) => r.risk_level === 'LOW').length,
  };

  /* ─── Render helpers ─────────────────────────────────────── */
  const RiskBadge = ({ level }: { level: RiskLevel }) => {
    const cfg = RISK_CONFIG[level];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border ${cfg.bg} ${cfg.border} ${cfg.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></span>
        {cfg.label}
      </span>
    );
  };

  const renderSectionTable = (section: SectionDef) => {
    const sectionRows = section.filter(rows);
    return (
      <div key={section.id} className={`rounded-xl border ${section.border} overflow-hidden`}>
        {/* Section header */}
        <div className={`${section.bg} px-5 py-3.5 flex items-center justify-between border-b ${section.border}`}>
          <div className="flex items-center gap-2.5">
            <span className={`w-8 h-8 flex items-center justify-center rounded-lg ${section.bg} ${section.color}`}>
              <i className={`${section.icon} text-sm`}></i>
            </span>
            <div>
              <h3 className={`text-sm font-semibold ${section.color}`}>{section.title}</h3>
              <p className="text-[10px] text-slate-400">{sectionRows.length} table{sectionRows.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {sectionRows.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-50 mx-auto mb-2">
                <i className={`${section.icon} text-slate-300 text-lg`}></i>
              </div>
              <p className="text-slate-400 text-xs">No tables in this category</p>
            </div>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Table</th>
                  <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 w-20">Schema</th>
                  <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 w-24">RLS</th>
                  <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 w-24">Tenant ID</th>
                  <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 w-24">Risk</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {sectionRows.map((row) => (
                  <tr key={row.table_name} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-5 py-2.5">
                      <span className="text-sm text-slate-800 font-medium font-mono">{row.table_name}</span>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className="text-[11px] text-slate-400 font-mono">{row.table_schema}</span>
                    </td>
                    <td className="px-5 py-2.5">
                      {row.has_rls ? (
                        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                          Enabled
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] text-rose-600 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-400"></span>
                          Disabled
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-2.5">
                      {row.has_tenant_id ? (
                        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300"></span>
                          No
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-2.5">
                      <RiskBadge level={row.risk_level} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  /* ─── Main ──────────────────────────────────────────────── */
  return (
    <SuperAdminLayout>
      <SAHeader
        title="Security Audit Dashboard"
        subtitle="Multi-tenant table integrity and RLS policy overview"
        actions={
          <button
            onClick={fetchAll}
            disabled={loading}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-slate-500 hover:text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer disabled:opacity-40 whitespace-nowrap"
          >
            <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`}></i>
            Refresh
          </button>
        }
      />

      <div className="px-4 md:px-8 pt-5 pb-8">
        {/* Last checked */}
        {lastChecked && (
          <p className="text-slate-400 text-xs mb-4 flex items-center gap-1.5">
            <i className="ri-time-line text-xs"></i>
            Last checked: {lastChecked.toLocaleTimeString()}
          </p>
        )}

        {/* ── Metadata Unavailable Banner ── */}
        {!loading && metadataUnavailable && (
          <div className="mb-5 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3">
            <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-information-line text-amber-500 text-base"></i>
            </span>
            <div>
              <p className="text-sm font-semibold text-amber-800 mb-0.5">Static Analysis Mode</p>
              <p className="text-xs text-amber-600 leading-relaxed">
                Security metadata requires backend/admin API access. The table analysis below is based on the known schema snapshot.
                For live RLS and column-level verification, use the Supabase dashboard SQL editor.
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <LoadingState text="Running security audit…" />
        ) : error ? (
          <ErrorState
            title="Audit check failed"
            message={error}
            onRetry={fetchAll}
            theme="dark"
          />
        ) : (
          <>
            {/* ── KPI Row ── */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
              {[
                { label: 'Total Tables',  value: kpis.total,          icon: 'ri-database-2-line', color: 'text-slate-600', bg: 'bg-slate-50',  border: 'border-slate-100' },
                { label: 'RLS Enabled',   value: kpis.rlsOn,          icon: 'ri-lock-line',        color: 'text-emerald-600',bg: 'bg-emerald-50', border: 'border-emerald-100' },
                { label: 'RLS Missing',   value: kpis.rlsOff,         icon: 'ri-alert-line',       color: 'text-rose-600',   bg: 'bg-rose-50',    border: 'border-rose-100' },
                { label: 'Has tenant_id', value: kpis.withTenant,     icon: 'ri-shield-check-line',color: 'text-emerald-600',bg: 'bg-emerald-50', border: 'border-emerald-100' },
                { label: 'No tenant_id',  value: kpis.withoutTenant,  icon: 'ri-shield-flash-line',color: 'text-amber-600',  bg: 'bg-amber-50',   border: 'border-amber-100' },
                { label: 'High Risk',     value: kpis.highRisk,       icon: 'ri-error-warning-line',color: 'text-rose-600',   bg: 'bg-rose-50',    border: 'border-rose-100' },
                { label: 'Medium Risk',   value: kpis.mediumRisk,     icon: 'ri-alert-line',       color: 'text-amber-600',  bg: 'bg-amber-50',   border: 'border-amber-100' },
                { label: 'Low Risk',      value: kpis.lowRisk,        icon: 'ri-check-line',       color: 'text-emerald-600',bg: 'bg-emerald-50', border: 'border-emerald-100' },
              ].map((kpi) => (
                <div key={kpi.label} className={`rounded-xl ${kpi.bg} border ${kpi.border} px-4 py-3`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`w-5 h-5 flex items-center justify-center ${kpi.color}`}>
                      <i className={`${kpi.icon} text-xs`}></i>
                    </span>
                    <span className="text-[10px] text-slate-500 font-medium">{kpi.label}</span>
                  </div>
                  <p className={`text-2xl font-black ${kpi.color} tabular-nums`}>{kpi.value}</p>
                </div>
              ))}
            </div>

            {/* ── Summary health bar ── */}
            <div className="bg-white rounded-xl border border-slate-200 px-5 py-4 mb-6">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-xs font-semibold text-slate-600">Overall RLS Coverage:</span>
                <div className="flex-1 min-w-[200px] bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-2 rounded-full bg-emerald-500 transition-all"
                    style={{ width: `${kpis.total > 0 ? Math.round((kpis.rlsOn / kpis.total) * 100) : 0}%` }}
                  ></div>
                </div>
                <span className="text-sm font-bold text-emerald-600 tabular-nums">
                  {kpis.total > 0 ? Math.round((kpis.rlsOn / kpis.total) * 100) : 0}%
                </span>
                <span className="text-[10px] text-slate-400">
                  ({kpis.rlsOn}/{kpis.total} tables)
                </span>
              </div>
            </div>

            {/* ── Sections ── */}
            <div className="space-y-6">
              {SECTIONS.map((section) => renderSectionTable(section))}
            </div>

            {/* ── Storage Buckets ── */}
            <div className="rounded-xl border border-slate-200 overflow-hidden mt-6">
              <div className="bg-slate-50 px-5 py-3.5 flex items-center justify-between border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                    <i className="ri-folder-3-line text-sm"></i>
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700">Storage Buckets</h3>
                    <p className="text-[10px] text-slate-400">
                      {bucketsError ? 'Could not retrieve' : `${buckets.length} bucket${buckets.length !== 1 ? 's' : ''}`}
                    </p>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                {bucketsError ? (
                  <div className="px-5 py-8 text-center">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-50 mx-auto mb-2">
                      <i className="ri-alert-line text-amber-400 text-lg"></i>
                    </div>
                    <p className="text-slate-400 text-xs mb-1">Storage bucket listing unavailable</p>
                    <p className="text-slate-300 text-[10px] font-mono">{bucketsError}</p>
                  </div>
                ) : buckets.length === 0 ? (
                  <div className="px-5 py-8 text-center">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-50 mx-auto mb-2">
                      <i className="ri-folder-3-line text-slate-300 text-lg"></i>
                    </div>
                    <p className="text-slate-400 text-xs">No storage buckets found</p>
                  </div>
                ) : (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Bucket Name</th>
                        <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 w-32">Visibility</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {buckets.map((b) => (
                        <tr key={b.name} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-5 py-2.5">
                            <div className="flex items-center gap-2">
                              <span className="w-5 h-5 flex items-center justify-center">
                                <i className="ri-folder-3-line text-slate-400 text-sm"></i>
                              </span>
                              <span className="text-sm text-slate-800 font-medium font-mono">{b.name}</span>
                            </div>
                          </td>
                          <td className="px-5 py-2.5">
                            {b.public ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-600 border border-amber-100">
                                <i className="ri-global-line text-[9px]"></i>
                                Public
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-600 border border-emerald-100">
                                <i className="ri-lock-line text-[9px]"></i>
                                Private
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </SuperAdminLayout>
  );
}